import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Pagination } from '@material-ui/lab';
import { Visibility, SwapHoriz } from '@material-ui/icons';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  TableContainer,
  Paper,
  Menu,
  MenuItem,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { IHeader } from './TableHeaderUtils';
import useStyles from './styles';

interface Row {
  _id: string;
  [key: string]: any;
}

interface Props {
  headers: IHeader[];
  rows: Row[];
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onView?: (id: string) => void;
  onStatusChange?: (id: string, status: string) => void; // New prop for status change
  onReassign?: (id: string) => void; // New prop for re-assign
  setCurrentPage: any;
  noOfPages: number;
  setCurrentId?: any;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  currentPage?: number;
  userType?: string;
}

const DynamicTable: React.FC<Props> = ({
  headers,
  rows,
  onEdit,
  onDelete,
  onView,
  onStatusChange, // Add to destructuring
  onReassign,
  currentPage,
  setCurrentPage,
  noOfPages,
  setCurrentId,
  setIsOpen,
  userType, // Add this prop
}) => {
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(noOfPages / rowsPerPage);
  const classes = useStyles();

  // State for status dropdown
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  useEffect(() => {
    if (currentPage) {
      setPage(currentPage);
    }
  }, [currentPage]);

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
    setCurrentPage(newPage);
  };

  const getNestedValue = (obj: any, paths: string) => {
    const pathList = paths.split('||').map((path) => path.trim());
    for (const path of pathList) {
      const value = path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
      if (value !== undefined && value !== null) {
        return value;
      }
    }
    return '-';
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLTableCellElement>, rowId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleStatusClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleStatusSelect = (status: string) => {
    if (onStatusChange && selectedRowId) {
      onStatusChange(selectedRowId, status);
    }
    handleStatusClose();
  };

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = rows;

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header.field} style={{ width: header.width }}>
                  {header.label}
                </TableCell>
              ))}
              {<TableCell style={{ width: 'auto' }}>Action</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => (
              <TableRow key={row._id}>
                {headers.map((header) => (
                  <TableCell
                    key={header.field}
                    onClick={
                      // Add click handler for status columns
                      header.field === 'isApproved' || header.field === 'isRejected'
                        ? (e) => handleStatusClick(e, row._id)
                        : undefined
                    }
                    style={{
                      cursor: header.field === 'isApproved' || header.field === 'isRejected' ? 'pointer' : 'default',
                    }}
                  >
                    {header.field === 'assignedTo'
                      ? row.assignedTo === 'DEPWD'
                        ? `${row.assignedTo}\n (${row.complaintMeta?.authority || '-'})`
                        : row.assignedTo === 'SCPD'
                        ? userType === 'ADMIN'
                          ? `${row.assignedTo}\n (${row.complaintMeta?.state || '-'})`
                          : `${row.complaintMeta?.district || '-'}`
                        : getNestedValue(row, header.field) || '-'
                      : getNestedValue(row, header.field) || '-'}
                  </TableCell>
                ))}
                <TableCell>
                  {onEdit && (
                    <IconButton title="Edit" onClick={() => onEdit?.(row._id)}>
                      <EditIcon />
                    </IconButton>
                  )}
                  {onDelete && (
                    <IconButton
                      title="Delete"
                      onClick={() => {
                        setCurrentId(row._id);
                        setIsOpen && setIsOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                  {onView && (
                    <IconButton title="View" onClick={() => onView?.(row._id)}>
                      <Visibility />
                    </IconButton>
                  )}
                  {onEdit && (
                    <IconButton title="Re-Assign" onClick={() => onReassign?.(row._id)}>
                      <SwapHoriz />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Status Selection Menu */}
      {onStatusChange && (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleStatusClose}>
          <MenuItem onClick={() => handleStatusSelect('Approved')}>Approve</MenuItem>
          <MenuItem onClick={() => handleStatusSelect('Rejected')}>Reject</MenuItem>
        </Menu>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
        <Pagination count={totalPages} page={page} onChange={handleChangePage} />
      </div>
    </>
  );
};

export default DynamicTable;
